import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { fromEvent, merge, Subject, Subscription } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

export interface IdleEvent {
  type: 'idle' | 'active';
  idleDurationMs?: number;
  at: Date;
}

/**
 * IdleDetectionService — Tracks user inactivity via DOM events.
 *
 * Fires an idle event when no user interaction is detected for
 * the configured threshold. Fires an active event when the user
 * returns.
 *
 * Default idle threshold: 2 hours (suitable for 24/7 kiosk mode)
 *
 * Typical usage — trigger soft reload after idle:
 *   idleDetection.idle$.subscribe(() => softReload.softReload());
 */
@Injectable({ providedIn: 'root' })
export class IdleDetectionService implements OnDestroy {
  /** Emits when the user becomes idle */
  readonly idle$ = new Subject<IdleEvent>();
  /** Emits when the user returns from idle */
  readonly active$ = new Subject<IdleEvent>();

  private readonly destroy$ = new Subject<void>();
  private activitySub: Subscription | null = null;
  private idleTimerId: ReturnType<typeof setTimeout> | null = null;
  private isIdle = false;
  private idleStartTime: number | null = null;
  private idleThresholdMs = 2 * 60 * 60 * 1000; // 2 hours default

  constructor(private readonly ngZone: NgZone) {}

  /**
   * Start idle tracking.
   * @param thresholdMs Inactivity duration (ms) before idle fires (default: 2h)
   */
  start(thresholdMs = 2 * 60 * 60 * 1000): void {
    this.idleThresholdMs = thresholdMs;

    this.ngZone.runOutsideAngular(() => {
      this.activitySub = merge(
        fromEvent(document, 'touchstart', { passive: true }),
        fromEvent(document, 'touchend',   { passive: true }),
        fromEvent(document, 'click'),
        fromEvent(document, 'keydown'),
        fromEvent(document, 'mousemove'),
        fromEvent(document, 'scroll',     { passive: true }),
        fromEvent(document, 'visibilitychange'),
      )
        .pipe(debounceTime(500), takeUntil(this.destroy$))
        .subscribe(() => this.onActivity());

      // Arm the initial timer
      this.resetIdleTimer();
    });

    console.log(`[IdleDetection] Started — threshold: ${thresholdMs / 60_000}min`);
  }

  stop(): void {
    this.clearIdleTimer();
    this.activitySub?.unsubscribe();
    this.activitySub = null;
    console.log('[IdleDetection] Stopped');
  }

  /** Whether the user is currently considered idle */
  get currentlyIdle(): boolean {
    return this.isIdle;
  }

  private onActivity(): void {
    if (this.isIdle) {
      // User returned from idle
      const idleDurationMs = this.idleStartTime ? Date.now() - this.idleStartTime : undefined;
      this.isIdle = false;
      this.idleStartTime = null;

      this.ngZone.run(() => {
        this.active$.next({ type: 'active', idleDurationMs, at: new Date() });
        console.log(
          `[IdleDetection] User returned after ${idleDurationMs ? Math.round(idleDurationMs / 60_000) + 'min' : 'unknown'} idle`
        );
      });
    }

    this.resetIdleTimer();
  }

  private resetIdleTimer(): void {
    this.clearIdleTimer();
    this.idleTimerId = setTimeout(() => this.onIdle(), this.idleThresholdMs);
  }

  private onIdle(): void {
    this.isIdle = true;
    this.idleStartTime = Date.now();

    this.ngZone.run(() => {
      const event: IdleEvent = { type: 'idle', at: new Date() };
      this.idle$.next(event);
      console.log(`[IdleDetection] User idle for ${this.idleThresholdMs / 60_000}min — emitting idle event`);
    });
  }

  private clearIdleTimer(): void {
    if (this.idleTimerId !== null) {
      clearTimeout(this.idleTimerId);
      this.idleTimerId = null;
    }
  }

  ngOnDestroy(): void {
    this.stop();
    this.destroy$.next();
    this.destroy$.complete();
    this.idle$.complete();
    this.active$.complete();
  }
}
